import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Search, Eye, UserCheck } from "lucide-react";
import axios from "axios";

const API = "/api";

const statusColors = {
  scheduled: "bg-indigo-100 text-indigo-700",
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  delivered: "bg-green-100 text-green-700",
  complete: "bg-teal-100 text-teal-700",
  cancelled: "bg-red-100 text-red-700"
};

const statusLabels = {
  scheduled: "Scheduled",
  pending: "Pending",
  confirmed: "Confirmed",
  processing: "Processing",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  complete: "Complete",
  cancelled: "Cancelled"
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` }
  });

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, getAuthHeaders());
      setOrders(response.data);
    } catch (error) {
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    let cancelReason;
    if (newStatus === "cancelled") {
      cancelReason = window.prompt("Enter the cancellation reason:");
      if (!cancelReason?.trim()) {
        toast.error("Cancellation reason is required");
        return;
      }
    }
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, {}, {
        ...getAuthHeaders(),
        params: { new_status: newStatus, ...(cancelReason ? { cancel_reason: cancelReason.trim() } : {}) },
      });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (phone && phone.startsWith("91") && phone.length === 12)
      return `+91 ${phone.slice(2, 7)} ${phone.slice(7)}`;
    return phone;
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch =
      o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.user_phone.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const orderStats = {
    total: orders.length,
    pending: orders.filter(o => o.status === "pending").length,
    delivered: orders.filter(o => o.status === "delivered").length,
    cancelled: orders.filter(o => o.status === "cancelled").length
  };

  return (
    <div data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCart className="text-[#6C42F5]" size={28} />
            Orders
          </h1>
          <p className="text-gray-600 mt-1">Manage customer orders</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-gray-900">{orderStats.total}</p>
          <p className="text-gray-500 text-sm">Total Orders</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-yellow-600">{orderStats.pending}</p>
          <p className="text-gray-500 text-sm">Pending</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-600">{orderStats.delivered}</p>
          <p className="text-gray-500 text-sm">Delivered</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-2xl font-bold text-red-600">{orderStats.cancelled}</p>
          <p className="text-gray-500 text-sm">Cancelled</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search by order ID, name, or phone…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-orders"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="filter-status">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#6C42F5]" size={32} />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm || statusFilter !== "all" ? "No orders found matching your filters" : "No orders yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700">Order ID</TableHead>
                  <TableHead className="font-bold text-gray-700">Customer</TableHead>
                  <TableHead className="font-bold text-gray-700">Items</TableHead>
                  <TableHead className="font-bold text-gray-700">Total</TableHead>
                  <TableHead className="font-bold text-gray-700">Payment</TableHead>
                  <TableHead className="font-bold text-gray-700">Assigned To</TableHead>
                  <TableHead className="font-bold text-gray-700">Status</TableHead>
                  <TableHead className="font-bold text-gray-700">Payment</TableHead>
                  <TableHead className="font-bold text-gray-700">Date</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-gray-50" data-testid={`order-row-${order.id}`}>
                    <TableCell className="font-mono text-sm text-gray-600">
                      {order.id.slice(0, 8)}…
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{order.user_name}</p>
                        <p className="text-gray-500 text-sm">{formatPhone(order.user_phone)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{order.order_type === "remove_connection" ? "Remove Connection" : `${order.total_items} items`}</TableCell>
                    <TableCell className="font-medium text-gray-900">{order.order_type === "remove_connection" ? "Service" : `₹${order.total_amount.toFixed(2)}`}</TableCell>
                    <TableCell className="text-gray-600">{order.order_type === "remove_connection" ? "—" : order.payment_method}</TableCell>
                    <TableCell>
                      {order.delivery_manager_name ? (
                        <span className="flex items-center gap-1.5 text-sm text-[#6C42F5] font-medium">
                          <UserCheck size={14} />
                          {order.delivery_manager_name}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={order.status}
                        onValueChange={(value) => handleStatusChange(order.id, value)}
                        disabled={updatingStatus}
                      >
                        <SelectTrigger className={`w-36 h-8 text-xs ${statusColors[order.status]}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="scheduled">Scheduled</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="complete">Complete</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {order.payment_status === "not_applicable" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                          Not applicable
                        </span>
                      ) : order.status === "cancelled" || order.payment_status === "cancelled" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Cancelled
                        </span>
                      ) : order.payment_status === "complete" ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          ✓ Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                          Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {order.scheduled_at ? `Scheduled: ${formatDate(order.scheduled_at)}` : formatDate(order.created_at)}
                    </TableCell>                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => navigate(`/admin/orders/${order.id}`)}
                        className="text-gray-600 hover:text-[#6C42F5]"
                        data-testid={`view-order-${order.id}`}
                      >
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
